import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import toast from 'react-hot-toast';

export default function ErrorLogs() {
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  const limit = 20;

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await client.get(
        `/api/v1/admin/error-logs?skip=${page * limit}&limit=${limit}`
      );
      setErrors(response.data.errors || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Hata logları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchErrors();
  }, [page, fetchErrors]);

  const handleSelectError = async (error) => {
    // Fetch full detail (includes stack_trace)
    try {
      const response = await client.get(`/api/v1/admin/error-logs/${error.id}`);
      setSelectedError(response.data);
    } catch {
      setSelectedError(error);
    }
  };

  const maxPages = Math.ceil(total / limit);
  const showingFrom = total > 0 ? page * limit + 1 : 0;
  const showingTo = Math.min((page + 1) * limit, total);

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">⚠️ Hata Logları</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Errors Table */}
        <div className={selectedError ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold text-slate-200">Endpoint</th>
                    <th className="px-6 py-3 text-left font-semibold text-slate-200">Durum</th>
                    <th className="px-6 py-3 text-left font-semibold text-slate-200">Hata</th>
                    <th className="px-6 py-3 text-left font-semibold text-slate-200">Zaman</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-slate-400">
                        Yükleniyor...
                      </td>
                    </tr>
                  ) : errors.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-slate-400">
                        Hata bulunamadı
                      </td>
                    </tr>
                  ) : (
                    errors.map((error) => (
                      <tr
                        key={error.id}
                        onClick={() => handleSelectError(error)}
                        className={`border-t border-slate-700 hover:bg-slate-700 cursor-pointer text-slate-300 ${
                          selectedError?.id === error.id ? 'bg-slate-700' : ''
                        }`}
                      >
                        <td className="px-6 py-3 font-mono text-xs">{error.endpoint}</td>
                        <td className="px-6 py-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              error.status_code >= 500
                                ? 'bg-red-600 text-white'
                                : error.status_code >= 400
                                ? 'bg-yellow-600 text-white'
                                : 'bg-blue-600 text-white'
                            }`}
                          >
                            {error.status_code}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-xs truncate max-w-xs">{error.error_type}</td>
                        <td className="px-6 py-3 text-xs text-slate-400 whitespace-nowrap">
                          {error.created_at ? new Date(error.created_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                <div className="text-slate-400 text-sm">
                  {showingFrom}–{showingTo} / {total} gösteriliyor
                </div>
                <div className="space-x-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50 text-sm"
                  >
                    ← Önceki
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= maxPages - 1}
                    className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50 text-sm"
                  >
                    Sonraki →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detail Panel */}
        {selectedError && (
          <div className="lg:col-span-1">
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Hata Detayları</h3>
                <button
                  onClick={() => setSelectedError(null)}
                  className="text-slate-400 hover:text-white text-sm"
                >
                  Kapat
                </button>
              </div>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-slate-400">Endpoint</p>
                  <p className="font-mono text-xs text-white break-all">
                    {selectedError.method} {selectedError.endpoint}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Durum Kodu</p>
                  <p className="text-white">{selectedError.status_code}</p>
                </div>
                <div>
                  <p className="text-slate-400">Hata Tipi</p>
                  <p className="text-white">{selectedError.error_type}</p>
                </div>
                <div>
                  <p className="text-slate-400">Mesaj</p>
                  <p className="text-white text-xs break-words">{selectedError.error_message}</p>
                </div>
                {selectedError.stack_trace && (
                  <div>
                    <p className="text-slate-400">Yığın İzi</p>
                    <pre className="text-xs text-slate-300 bg-slate-900 rounded p-2 mt-1 overflow-auto max-h-64 whitespace-pre-wrap">
                      {selectedError.stack_trace}
                    </pre>
                  </div>
                )}
                <div>
                  <p className="text-slate-400">IP</p>
                  <p className="text-white">{selectedError.ip_address || 'N/A'}</p>
                </div>
                {selectedError.user_id && (
                  <div>
                    <p className="text-slate-400">Kullanıcı ID</p>
                    <p className="font-mono text-xs text-white">{selectedError.user_id}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
